import { getTypeNameByContentType } from 'core/utils/lessonItems';

/**
 * Enriches the `extraProperties` payload for the `open_content` analytics event
 * (TAN-9876) so that Santillana's downstream Kinesis pipeline consistently
 * receives content_id, content_name, content_type and didactic_type.
 *
 * - content_id: alias of content_guid (already sent at every call site).
 * - content_type: mapped from content_type_guid via getTypeNameByContentType
 *   when the caller hasn't already supplied an explicit human-readable value.
 * - content_name: an explicit content_name wins; otherwise falls back to the
 *   payload's `name` (present at every call site), then `null`. Centralizing
 *   this fallback here means call sites don't need to duplicate
 *   `content_name: name` alongside `name` themselves.
 * - didactic_type: passed through as given, normalized to `null` when the
 *   caller doesn't have a value (keeps the outgoing JSON shape consistent
 *   instead of sometimes-present/sometimes-absent keys).
 *
 * This function does not invent values a call site can't supply — it only
 * derives content_type from content_type_guid and aliases content_id.
 */
function resolveContentType(explicitContentType: unknown, contentTypeGuid: unknown): unknown {
  if (explicitContentType != null) {
    return explicitContentType;
  }
  if (!contentTypeGuid) {
    return null;
  }
  return getTypeNameByContentType(contentTypeGuid as string) ?? null;
}

export function enrichOpenContentProperties(extraProperties: Record<string, unknown> = {}): Record<string, unknown> {
  const { content_guid, content_type, content_type_guid, content_name, didactic_type, name, ...rest } = extraProperties;

  return {
    ...rest,
    name,
    content_guid,
    content_id: content_guid ?? null,
    content_type_guid,
    content_type: resolveContentType(content_type, content_type_guid),
    content_name: content_name ?? name ?? null,
    didactic_type: didactic_type ?? null,
  };
}
